import { connect, useDispatch } from "react-redux";
import { Dispatch, Action } from "redux";
import { useTheme } from "styled-components";
import useIsMobile from "../../../../hooks/deviceChecker";
import { useLayout } from "../../../../provider/LayoutProvider";
import { useEffect, useMemo } from "react";
import Button from "../../../../components/Button";
import Valoration from "../../../../components/Valoration";
import Units from "../../../../components/Units";
import { Content, Title, Cover, CoverContainer } from "./styles";
import Typography from "../../../../components/Typography";
import Icon from "../../../../components/Icon";
import { Flex } from "../../../../components/StyledComponents";
import PlayIcon from "../../../../assets/icons/icon-cool/filled/Player/Play.svg";
import LeftArrow from "../../../../assets/icons/arrow-small-left.svg";
import { useParams, useNavigate } from "react-router";
import { getOneCourse } from "../../../../store/actions/course";
import { CourseDto, LabUnit } from "../../../../types/course.dto";
import InformationCircleIcon from "../../../../assets/icons/icon-cool/filled/InformationCircle.svg";
import noImageIcon from "../../../../assets/images/mca-lab-transparent.png";
import { createNewEnrollment } from "../../../../store/actions/enrollment";
import { NewEnrollmentInDto } from "../../../../types/enrollment.dto";
import Loader from "../../../../components/Spinner";
import CourseImage from "../../../../components/CourseImage";
import Loading from "../../../../components/Loading";

interface ComponentProps {
  course: CourseDto;
  courseStates: {
    loading: boolean;
  };
  newEnrollment: NewEnrollmentInDto;
  newEnrollmentStates: {
    loading: boolean;
    success: boolean;
  };
}

const Component = ({ course, courseStates, newEnrollmentStates }: ComponentProps) => {
  const dispatch = useDispatch<Dispatch<Action>>();
  const { layout, setLayout, setIsPageLoading } = useLayout();
  const theme = useTheme() as { [key: string]: { [key: string]: string } };
  const params = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  const pageLayout = useMemo(() => {
    return {
      ...layout,
      showSubHeader: false,
      showFilters: false,
      showSearchBar: false,
    };
  }, [isMobile]);

  const firstCourseUnit = useMemo((): LabUnit | undefined => {
    if (!course || !course.labUnit) return undefined;

    // Crear una copia de labUnit y ordenar
    const sortedLabUnits = [...course.labUnit].sort((a, b) => a.sort_order - b.sort_order);
    const firstUnit = sortedLabUnits[0];

    if (firstUnit && firstUnit.labComponent) {
      // Crear una copia de labComponent y ordenar
      firstUnit.labComponent = firstUnit.labComponent.map(component => ({ ...component }));
    }

    return firstUnit;
  }, [course]);

  useEffect(() => {
    setLayout(pageLayout);
  }, [pageLayout]);

  useEffect(() => {
    if (params && params.idCourse) {
      dispatch(getOneCourse({ idCourse: Number(params.idCourse) }));
    }
  }, [params.idCourse]);

  useEffect(() => {
    if (newEnrollmentStates.success) {
      navigate(
        `/dashboard/mis-cursos/${course?.id}/${firstCourseUnit?.id}/${firstCourseUnit?.labComponent[0]?.id}`
      );
    }

    if (newEnrollmentStates.loading) {
      setIsPageLoading(true);
    }
  }, [newEnrollmentStates]);

  const goBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    if (!course || courseStates.loading) {
      setIsPageLoading(true);
    } else {
      setIsPageLoading(false);
    }
  }, [courseStates, course]);

  return (
    <>
      <CoverContainer hasImage={!!course?.course_image_url}>
        <CourseImage courseId={course?.id} imageId={course?.image_id || null} isCard={false} />
      </CoverContainer>
      <Content>
        <Flex align="space">
          <div style={{ marginRight: 9, cursor: "pointer" }} onClick={goBack}>
            <Icon icon={LeftArrow} size="35px" color={theme.colors.Black} />
          </div>
          <Flex align="left" column>
            <Title>{course?.title}</Title>
            <div style={{ height: 7 }} />
            <Valoration value={course?.avg_rating} />
          </Flex>

          <Button
            options={{
              type: "filled",
              skin: "primary",
              size: "md",
            }}
            onClick={() =>
              dispatch(createNewEnrollment({ idCourse: course?.id }))
            }
            style={{ minWidth: 76, justifyContent: "space-between" }}
          >
            <Flex align="center" justify="center" gap={5}>
              {newEnrollmentStates.loading ? (
                <Loader style={{ zoom: 0.5 }} />
              ) : (
                <>
                  <Typography as="p" size="xs">
                    Iniciar
                  </Typography>
                  <Icon
                    icon={PlayIcon}
                    size="14px"
                    color={theme.colors.White}
                  />
                </>
              )}
            </Flex>
          </Button>
        </Flex>

        <div style={{ height: 10 }} />

        <Flex align="center" gap={10}>
          <div style={{ flexShrink: 0 }}>
            Complejidad: {course?.labLevel?.level}
          </div>
          <Flex align="center" gap={5}>
            {course?.enrollment_requirement === "Mandatory" && (
              <>
                <Icon
                  icon={InformationCircleIcon}
                  size="12px"
                  color={theme.colors.Warning}
                />
                <Typography as="p" size="xs">
                  Obligatorio
                </Typography>
              </>
            )}

            {course?.enrollment_requirement === "Optional" && (
              <Typography as="p" size="xs">
                Optativo
              </Typography>
            )}
          </Flex>
        </Flex>

        <div style={{ height: 10 }} />

        <Typography as="p" size="sm" style={{ lineHeight: "24.5px" }}>
          {course?.description}
        </Typography>

        <div style={{ height: 20 }} />

        <Units course={course} />
      </Content>
    </>
  );
};

const state = ({ courseStore, enrollmentStore }) => {
  const { data: course, states: courseStates } = courseStore.getOne;
  const { data: newEnrollment, states: newEnrollmentStates } =
    enrollmentStore.new;

  return {
    course,
    courseStates,
    newEnrollment,
    newEnrollmentStates,
  };
};

export default connect(state)(Component);
